import React, { useState, useEffect, useRef } from "react";
import { ArrowLeft, X } from "lucide-react";
import LavaRunner from "./games/LavaRunnerGame";
import TriviaRelampago from "./games/TriviaRelampago";

// ─── Game catalogue ──────────────────────────────────────────────────────────
const GAMES = [
  {
    id: "lava-runner",
    title: "Lava Runner",
    emoji: "🌋",
    tag: "ACCIÓN",
    available: true,
    shortDesc: "Corre automáticamente, salta y evita caer en la lava. Plataformas infinitas generadas proceduralmente.",
    image: "https://res.cloudinary.com/dnmygv8u6/image/upload/v1755502251/LavaRunnerFoto_t9obwf.png",
    accentColor: "#f97316",
    glowColor: "rgba(249,115,22,0.30)",
  },
  {
    id: "trivia-relampago",
    title: "Trivia Relámpago",
    emoji: "⚡",
    tag: "TRIVIA",
    available: true,
    shortDesc: "Responde preguntas a contrarreloj en distintas categorías y compite en el ranking global.",
    image: "https://res.cloudinary.com/dnmygv8u6/image/upload/v1755502488/TriviaRelampagoFoto_yxfmbc.png",
    accentColor: "#a855f7",
    glowColor: "rgba(168,85,247,0.30)",
  },
  {
    id: "scrap-bots",
    title: "Scrap Bots",
    emoji: "⚙️",
    tag: "PRÓXIMAMENTE",
    available: false,
    shortDesc: "Combate 3D con robots personalizados. Mejora tu máquina y conquista las arenas.",
    image: "https://res.cloudinary.com/dnmygv8u6/image/upload/v1755502982/coming_q9ah6s.jpg",
    accentColor: "#6b7280",
    glowColor: "rgba(107,114,128,0.15)",
  },
];

// ─── Static star field (generated once at module load) ───────────────────────
const STARS = Array.from({ length: 160 }, (_, i) => ({
  id: i,
  left: Math.random() * 100,
  top: Math.random() * 100,
  size: Math.random() * 1.6 + 0.5,
  dur: 2.2 + Math.random() * 3,
  delay: Math.random() * 4,
}));

// ─── Keyframes (injected once) ───────────────────────────────────────────────
const GZ_CSS = `
  @keyframes gz-twinkle {
    0%, 100% { opacity: 0.12; transform: scale(1); }
    50%       { opacity: 0.80; transform: scale(1.4); }
  }
  @keyframes gz-fadein {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes gz-rise {
    from { opacity: 0; transform: translateY(28px) scale(0.88); }
    to   { opacity: 1; transform: translateY(0)     scale(1);    }
  }
  @keyframes gz-glow-pulse {
    0%, 100% { filter: drop-shadow(0 0 28px rgba(167,139,250,0.55)); }
    50%       { filter: drop-shadow(0 0 54px rgba(167,139,250,0.90)); }
  }
  @keyframes gz-bar {
    from { width: 0%; }
    to   { width: 100%; }
  }
  @keyframes gz-scanline {
    from { transform: translateY(-100%); }
    to   { transform: translateY(100vh); }
  }
`;

// ─── Shared star field component ─────────────────────────────────────────────
function StarField({ opacity = 1 }) {
  return (
    <div
      className="absolute inset-0 pointer-events-none overflow-hidden"
      style={{ opacity }}
      aria-hidden="true"
    >
      {STARS.map((s) => (
        <div
          key={s.id}
          className="absolute rounded-full bg-white"
          style={{
            left: `${s.left}%`,
            top: `${s.top}%`,
            width: s.size,
            height: s.size,
            animation: `gz-twinkle ${s.dur}s ease-in-out ${s.delay}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

// ─── Intro Screen ────────────────────────────────────────────────────────────
function IntroScreen({ onDone }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const timers = [
      setTimeout(() => setStep(1), 180),   // GAME ZONE rises
      setTimeout(() => setStep(2), 1100),  // SIT / subtitle appears
      setTimeout(() => setStep(3), 1900),  // loading bar starts
      setTimeout(() => setStep(4), 2750),  // fade out begins
      setTimeout(onDone, 3200),
    ];
    return () => timers.forEach(clearTimeout);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center select-none"
      style={{
        opacity: step >= 4 ? 0 : 1,
        transition: step >= 4 ? "opacity 0.45s ease" : "none",
      }}
    >
      <StarField />

      {/* Nebula glow behind text */}
      <div
        className="absolute pointer-events-none rounded-full"
        style={{
          width: "min(700px, 95vw)",
          height: "min(700px, 95vw)",
          background:
            "radial-gradient(circle, rgba(109,40,217,0.16) 0%, rgba(59,130,246,0.07) 45%, transparent 70%)",
          opacity: step >= 1 ? 1 : 0,
          transition: "opacity 1.4s ease",
        }}
      />

      {/* Scanline decoration */}
      <div
        className="absolute inset-x-0 h-px pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(167,139,250,0.25), rgba(56,189,248,0.25), transparent)",
          animation: step >= 1 ? "gz-scanline 3s linear forwards" : "none",
        }}
      />

      {/* Main text block */}
      <div className="relative z-10 text-center px-4">
        {/* GAME ZONE */}
        <div
          style={{
            animation: step >= 1 ? "gz-rise 0.75s cubic-bezier(0.34,1.56,0.64,1) forwards" : "none",
            opacity: step >= 1 ? undefined : 0,
          }}
        >
          <h1
            className="font-black leading-none"
            style={{
              fontSize: "clamp(2.6rem, 13vw, 7.5rem)",
              background:
                "linear-gradient(135deg, #ffffff 0%, #c4b5fd 28%, #818cf8 58%, #38bdf8 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              letterSpacing: "0.11em",
              animation: step >= 1 ? "gz-glow-pulse 2.4s ease-in-out 0.4s infinite" : "none",
            }}
          >
            GAME ZONE
          </h1>
        </div>

        {/* SIT · PLATAFORMA ESTUDIANTIL */}
        <div
          style={{
            opacity: step >= 2 ? 1 : 0,
            transform: step >= 2 ? "translateY(0)" : "translateY(14px)",
            transition: "opacity 0.55s ease, transform 0.55s ease",
            marginTop: "0.9rem",
          }}
        >
          <p
            className="font-semibold tracking-[0.55em] text-white/45 uppercase"
            style={{ fontSize: "clamp(0.7rem, 2.4vw, 1.05rem)" }}
          >
            S I T &nbsp;·&nbsp; PLATAFORMA ESTUDIANTIL
          </p>
        </div>

        {/* Loading bar */}
        <div
          className="mt-10 mx-auto rounded-full overflow-hidden"
          style={{
            width: "clamp(90px, 22vw, 170px)",
            height: 2,
            background: "rgba(255,255,255,0.08)",
            opacity: step >= 2 ? 1 : 0,
            transition: "opacity 0.4s ease",
          }}
        >
          <div
            className="h-full rounded-full"
            style={{
              background: "linear-gradient(90deg, #a78bfa, #38bdf8)",
              animation: step >= 3 ? "gz-bar 0.85s ease forwards" : "none",
            }}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Game Card ────────────────────────────────────────────────────────────────
function GameCard({ game, onSelect }) {
  const [hovered, setHovered] = useState(false);
  const active = game.available && hovered;

  return (
    <div
      role={game.available ? "button" : undefined}
      tabIndex={game.available ? 0 : -1}
      aria-label={game.available ? `Jugar ${game.title}` : `${game.title} - próximamente`}
      className="relative rounded-2xl overflow-hidden cursor-pointer select-none outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
      style={{
        background: "rgba(255,255,255,0.03)",
        border: `1px solid ${active ? game.accentColor + "55" : "rgba(255,255,255,0.07)"}`,
        boxShadow: active
          ? `0 0 28px ${game.glowColor}, 0 10px 36px rgba(0,0,0,0.55)`
          : "0 4px 18px rgba(0,0,0,0.45)",
        transform: active ? "translateY(-5px) scale(1.015)" : "translateY(0) scale(1)",
        transition: "all 0.25s ease",
        opacity: game.available ? 1 : 0.5,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => game.available && onSelect(game)}
      onKeyDown={(e) => e.key === "Enter" && game.available && onSelect(game)}
    >
      {/* Cover image */}
      <div className="relative overflow-hidden" style={{ height: "clamp(140px, 22vw, 188px)" }}>
        <img
          src={game.image}
          alt={game.title}
          loading="lazy"
          className="w-full h-full object-cover"
          style={{
            transform: active ? "scale(1.07)" : "scale(1)",
            transition: "transform 0.45s ease",
            filter: game.available ? "none" : "grayscale(55%)",
          }}
        />
        {/* Gradient overlay */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.05) 30%, rgba(0,0,0,0.80) 100%)",
          }}
        />
        {/* Tag badge */}
        <span
          className="absolute top-3 left-3 text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded"
          style={{
            background: `${game.accentColor}28`,
            color: game.available ? game.accentColor : "#9ca3af",
            border: `1px solid ${game.accentColor}38`,
          }}
        >
          {game.tag}
        </span>
      </div>

      {/* Info */}
      <div className="p-4">
        <h3 className="font-bold text-white mb-1 text-sm sm:text-base leading-snug">
          {game.emoji} {game.title}
        </h3>
        <p className="text-white/45 text-xs leading-relaxed">{game.shortDesc}</p>

        <div className="mt-3.5">
          {game.available ? (
            <span
              className="text-xs font-semibold flex items-center gap-1.5"
              style={{ color: game.accentColor }}
            >
              <span>▶</span> Jugar ahora
            </span>
          ) : (
            <span className="text-xs text-white/22 font-medium">En desarrollo...</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Menu Screen ──────────────────────────────────────────────────────────────
function MenuScreen({ onSelectGame, onClose }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className="absolute inset-0 flex flex-col overflow-hidden"
      style={{
        opacity: visible ? 1 : 0,
        transition: "opacity 0.5s ease",
      }}
    >
      <StarField opacity={0.45} />

      {/* Top ambient glow */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 pointer-events-none"
        aria-hidden="true"
        style={{
          width: "85vw",
          height: "40vh",
          background:
            "radial-gradient(ellipse at top, rgba(109,40,217,0.10) 0%, transparent 70%)",
        }}
      />

      {/* Header bar */}
      <header className="relative z-10 flex items-center justify-between px-4 sm:px-8 py-4 sm:py-5 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <span
            className="font-black tracking-widest truncate"
            style={{
              background: "linear-gradient(90deg, #c4b5fd 0%, #818cf8 60%, #38bdf8 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              fontSize: "clamp(0.95rem, 3.5vw, 1.45rem)",
              letterSpacing: "0.18em",
            }}
          >
            GAME ZONE
          </span>
          <span
            className="hidden sm:inline font-light text-white/28 tracking-[0.4em] uppercase flex-shrink-0"
            style={{ fontSize: "0.62rem" }}
          >
            by SIT
          </span>
        </div>

        <button
          onClick={onClose}
          className="w-9 h-9 flex-shrink-0 rounded-full flex items-center justify-center text-white/45 hover:text-white hover:bg-white/10 active:scale-90 transition-all"
          aria-label="Cerrar GameZone"
        >
          <X size={20} />
        </button>
      </header>

      {/* Separator */}
      <div
        className="relative z-10 flex-shrink-0 h-px mx-4 sm:mx-8"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(167,139,250,0.25), rgba(56,189,248,0.15), transparent)",
        }}
      />

      {/* Game grid */}
      <div className="relative z-10 flex-1 overflow-y-auto px-4 sm:px-8 py-6 sm:py-8">
        <p
          className="text-center text-white/28 text-xs tracking-[0.35em] uppercase mb-7 sm:mb-9"
          style={{ letterSpacing: "0.32em" }}
        >
          Elige tu juego
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto pb-4">
          {GAMES.map((game) => (
            <GameCard key={game.id} game={game} onSelect={onSelectGame} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Playing Screen ───────────────────────────────────────────────────────────
function PlayingScreen({ game, onBack }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className="absolute inset-0 flex flex-col bg-black"
      style={{
        opacity: visible ? 1 : 0,
        transition: "opacity 0.3s ease",
      }}
    >
      {/* Mini header */}
      <div
        className="flex items-center gap-2 px-3 py-2 flex-shrink-0"
        style={{
          background: "rgba(0,0,0,0.65)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <button
          onClick={onBack}
          className="w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center text-white/55 hover:text-white hover:bg-white/10 active:scale-90 transition-all"
          aria-label="Volver al menú de juegos"
        >
          <ArrowLeft size={17} />
        </button>
        <span
          className="text-white/40 text-xs font-semibold tracking-widest uppercase truncate"
          style={{ letterSpacing: "0.22em" }}
        >
          {game.emoji} {game.title}
        </span>
      </div>

      {/* Game content — fills all remaining space */}
      <div className="flex-1 min-h-0 overflow-hidden relative">
        {game.id === "lava-runner" && <LavaRunner onClose={onBack} />}
        {game.id === "trivia-relampago" && <TriviaRelampago onClose={onBack} />}
      </div>
    </div>
  );
}

// ─── Root Component ───────────────────────────────────────────────────────────
export default function GameZoneMenu({ isOpen, onClose }) {
  const [phase, setPhase] = useState("intro"); // intro | menu | playing
  const [selectedGame, setSelectedGame] = useState(null);
  const styleInjected = useRef(false);

  // Inject CSS keyframes once
  if (!styleInjected.current) {
    styleInjected.current = true;
    if (typeof document !== "undefined") {
      const id = "gz-keyframes";
      if (!document.getElementById(id)) {
        const el = document.createElement("style");
        el.id = id;
        el.textContent = GZ_CSS;
        document.head.appendChild(el);
      }
    }
  }

  // Reset state whenever the overlay opens
  useEffect(() => {
    if (!isOpen) return;
    setPhase("intro");
    setSelectedGame(null);
  }, [isOpen]);

  // ESC key closes overlay
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black overflow-hidden" role="dialog" aria-modal="true" aria-label="GameZone">
      {phase === "intro" && (
        <IntroScreen onDone={() => setPhase("menu")} />
      )}

      {phase === "menu" && (
        <MenuScreen
          onSelectGame={(game) => {
            setSelectedGame(game);
            setPhase("playing");
          }}
          onClose={onClose}
        />
      )}

      {phase === "playing" && selectedGame && (
        <PlayingScreen
          game={selectedGame}
          onBack={() => {
            setSelectedGame(null);
            setPhase("menu");
          }}
        />
      )}
    </div>
  );
}
